<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-flag'"
      :section-title="$tc('configuration.sections.priorities', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-testing="'activate'"
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          aria-hidden="true"
          class="far fa-check-circle"
        />
      </b-button>
      <b-button
        v-testing="'deactivate'"
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          aria-hidden="true"
          class="far fa-times-circle"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-testing="'delete'"
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deletePriorities(selectedRows)"
        @shortkey="deletePriorities(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'update'"
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showPriorityModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showPriorityModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'add'"
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showPriorityModal($t('configuration.actions.add'))"
        @shortkey="showPriorityModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="priorityModal">
      <priority-modal
        :action-title="actionTitle"
        @update="patchPriorities($event)"
        @create="createPriority($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsPrioritiesGrid from '@/modules/configurationModule/detail/cases/priorities/columnsPrioritiesGrid'
import PriorityModal from '@/modules/configurationModule/detail/cases/priorities/PriorityModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import PriorityConfigurationServices from '@/api/resources/configuration/cases/priority/PriorityConfigurationServices'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import sessionStore from '@/session/sessionStore'

export default {
    name: 'CasePrioritiesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        PriorityModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.cases.priorities',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsPrioritiesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActivePriority() {
            return !!this.selectedRows.find(priority => priority.active)
        },
        hasDeactivePriority() {
            return !!this.selectedRows.find(priority => !priority.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(priority => priority.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(priority => !priority.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActivePriority && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivePriority && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const prioritiesToPatch =
                selectionBatch
                    .filter(priority => priority.active !== option)
                    .map(priority => ({
                        active: option,
                        id: priority.id
                    }))
            if (prioritiesToPatch.length) {
                const response =
                    await this.$_toCallUnique(
                        PriorityConfigurationServices.patchPrioritiesBatch(prioritiesToPatch))
                if (response !== null) {
                    this.$_showToastSuccess(
                        this.$tc('configuration.toastMessages.updated',
                            prioritiesToPatch.length,
                            { elements: prioritiesToPatch.length }))
                    await this.reloadData()
                }
            }
        },
        async patchPriorities(...priorities) {
            const priorityPatchInBatchRequests =
                priorities.map(priority => ({
                    id: priority.id,
                    active: priority.active,
                    code: priority.code,
                    level: Number(priority.level),
                    color: priority.color,
                    nameTranslations: priority.nameTranslations.map(nameTranslation => ({
                        languageId: nameTranslation.language.id,
                        notes: nameTranslation.notes,
                        value: nameTranslation.value
                    }))
                }))
            const response =
                await this.$_toCallUnique(
                    PriorityConfigurationServices.patchPrioritiesBatch(priorityPatchInBatchRequests))
            if (response !== null) {
                this.$refs.priorityModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                await this.reloadData()
            }
        },
        async createPriority(priority) {
            const priorityCreateRequest = {
                active: priority.active,
                code: priority.code,
                level: Number(priority.level),
                color: priority.color,
                nameTranslations: priority.nameTranslations.map(nameTranslation => ({
                    languageId: nameTranslation.language.id,
                    notes: nameTranslation.notes,
                    value: nameTranslation.value
                }))
            }
            const response =
                await this.$_toCallUnique(
                    PriorityConfigurationServices.createPriority(priorityCreateRequest))
            if (response !== null) {
                this.$refs.priorityModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                await this.reloadData()
            }
        },
        async deletePrioritiesBatch(priorities) {
            const prioritiesToDelete = priorities.map(priority => priority.id)
            const response =
                await this.$_toCallUnique(
                    PriorityConfigurationServices.deletePrioritiesBatch(prioritiesToDelete))
            if (response !== null) {
                this.$_showToastSuccess(
                    this.$tc(
                        'configuration.toastMessages.deleted',
                        prioritiesToDelete.length,
                        { elements: prioritiesToDelete.length }))
                await this.reloadData()
            }
        },
        async reloadData() {
            await sessionStore.updatePriorities()
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(PriorityConfigurationServices))
        },
        getContextMenuItems(params) {
            if (params?.node) {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showPriorityModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deletePriorities(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
            return []
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(PriorityConfigurationServices))
        },
        showPriorityModal(title, selectedPriority) {
            this.actionTitle = title
            this.$refs.priorityModal.show([selectedPriority])
        },
        async deletePriorities(priorities) {
            if (priorities.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deletePrioritiesBatch(priorities)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
